import { useCallback, useState } from 'react'

/**
 * Preferencias de vista del usuario: densidad, columnas visibles, orden.
 *
 * "La tabla recuerda las columnas, el orden y los filtros de cada usuario.
 * Pequeño, invisible en una demo, y lo primero que se agradece al tercer día."
 *
 * Los **filtros** viven en la URL, no aquí: así la vista se comparte por enlace.
 * Aquí vive lo que es preferencia del usuario y no del enlace.
 *
 * De momento se guardan en el navegador. Cuando exista
 * `GET/PUT /interna/preferencias` se cambia la implementación de este hook y
 * ninguna pantalla se entera.
 */

const PREFIJO = 'devtek.ecf.'

export function leerPreferencia<T>(clave: string, porDefecto: T): T {
  try {
    const crudo = window.localStorage.getItem(PREFIJO + clave)
    if (crudo === null) return porDefecto
    return JSON.parse(crudo) as T
  } catch {
    return porDefecto
  }
}

export function escribirPreferencia<T>(clave: string, valor: T): void {
  try {
    window.localStorage.setItem(PREFIJO + clave, JSON.stringify(valor))
  } catch {
    // Modo privado o cuota llena: la preferencia se pierde, la aplicación no.
  }
}

export function usePreferencia<T>(clave: string, porDefecto: T): [T, (valor: T) => void] {
  const [valor, setValor] = useState<T>(() => leerPreferencia(clave, porDefecto))

  const guardar = useCallback(
    (nuevo: T) => {
      setValor(nuevo)
      escribirPreferencia(clave, nuevo)
    },
    [clave],
  )

  return [valor, guardar]
}
